import axios from 'axios';

import API, {
  GET_ROLE_AUDIT_LOG,
  GET_ROLE_AUDIT_LOG_VERSIONS,
  GET_SESSION_HISTORY,
  GET_USER_AUDIT_LOG,
  GET_USER_AUDIT_LOG_VERSIONS
} from '~/api/endpointsAPI';
import GRPC_USER from '~/common/grpc/UserService';
import { getGrpcMessage } from '~/common/grpc/helpers';
import {
  mapToSessionHistoryModel,
  mapToLastAccessTimeModel
} from './userAPI.mapper';
import { USER_ORGANIZATION_TYPE } from '~/common/constants';
import text from './text';

export const inviteUser = user => postRequestHandler(API.INVITE_USER, user);

export const createUser = user => postRequestHandler(API.CREATE_USER, user);

export const createUserWithRoles = user =>
  postRequestHandler(API.CREATE_USER_WITH_ROLES, user);

export const updateUser = user =>
  postRequestHandler(API.UPDATE_USER, user, text.defaultMsg);

export const setPassword = async (password, hash) => {
  const response = await axios.post(API.SET_PASSWORD, {
    new_password: password,
    link_hash: hash
  });
  return response;
};

export const setPasswordFirstTimeExt = (login, oldPassword, newPassword) =>
  postRequestHandler(API.CHANGE_PASSWORD_FIRST_TIME_EXT, {
    login,
    oldPassword,
    newPassword
  });

export const requestResetPassword = request =>
  postRequestHandler(API.REQEST_RESET_PASSWORD, request);

export const forceResetPassword = (login, newPassword) =>
  postRequestHandler(API.FORCE_RESET_PASSWORD, { login, newPassword });

export const fetchPasswordValidationRules = () => {
  return Promise.resolve({
    minLength: 8,
    capitalLetter: true,
    specialCharacter: true,
    number: true
  });
  // postRequestHandler(API.PASS_VALIDATION_RULES);
};

export const updatePassword = (login, oldPassword, newPassword) =>
  postRequestHandler(
    API.CHANGE_PASSWORD,
    {
      login,
      oldPassword,
      newPassword
    },
    text.defaultUpdatePassword
  );

export const getUser = login =>
  postRequestHandler(API.GET_USER, { login }, text.defaultMsg);
export const getUserAuditLogVersions = async login => {
  try {
    const url = GET_USER_AUDIT_LOG_VERSIONS(login);
    const result = await axios.get(url);
    return result;
  } catch (error) {
    return getReadableError(error);
  }
};

export const getUserAuditLog = async (login, version) => {
  try {
    const url = GET_USER_AUDIT_LOG(login, version);
    const result = await axios.get(url);
    return result;
  } catch (error) {
    return getReadableError(error);
  }
};

export const getRoleAuditLogVersions = async login => {
  try {
    const url = GET_ROLE_AUDIT_LOG_VERSIONS(login);
    const result = await axios.get(url);
    return result;
  } catch (error) {
    return getReadableError(error);
  }
};

export const getRoleAuditLog = async (login, version) => {
  try {
    const url = GET_ROLE_AUDIT_LOG(login, version);
    const result = await axios.get(url);
    return result;
  } catch (error) {
    return getReadableError(error);
  }
};
export const getUserProfile = () => postRequestHandler(API.GET_USER_PROFILE);

export const getCurrentUser = () => postRequestHandler(API.GET_CURRENT_USER);

export const checkIfUserExist = login =>
  postRequestHandler(API.CHECK_USER_EXIST, { login });

export const deleteUser = (login, reason) =>
  postRequestHandler(API.DELETE_USER, { login, reason }, text.defaultDelete);

export const acceptInvitattion = (password, hash) =>
  axios.post(API.ACCEPT_INVITATION, {
    new_password: password,
    link_hash: hash
  });

export const verifyInvitattion = (hash, email) =>
  axios.post(API.VERIFY_INVITATION, {
    link_hash: hash,
    email: email
  });

export const getApplications = async login => {
  try {
    const result = await axios.get(API.APP_ACCESS, { params: { login } });
    return result.data.applications || [];
  } catch (error) {
    return getReadableError(error);
  }
};

const getData = (url, mapResponse = data => data) => async params => {
  try {
    const result = await axios.get(url, { params });
    return mapResponse(result.data);
  } catch (error) {
    return getReadableError(error);
  }
};

export const getSessionHistory = (userId, params) =>
  getData(GET_SESSION_HISTORY(userId), mapToSessionHistoryModel)(params);

export const getUserGroups = () =>
  postRequestHandler(API.GET_USER_GROUPS, {}, text.userGroup);

export const getUserOrgSuggestions = () =>
  postRequestHandler(API.GET_USER_ORG_SUGGESTIONS, {}, text.userOrgSuggestions);

export const findUsers = searchData =>
  postRequestHandler(API.FIND_USERS, searchData);

export const findExternalUsers = searchData =>
  findUsers({
    ...searchData,
    organizationType: USER_ORGANIZATION_TYPE.EXTERNAL
  });

export const findInternalUsers = searchData =>
  findUsers({
    ...searchData,
    organizationType: USER_ORGANIZATION_TYPE.INTERNAL
  });

export const activateUser = data => postRequestHandler(API.ACTIVATE_USER, data);

export const getUnlockUserReasons = async () =>
  postRequestHandler(API.GET_UNLOCK_REASONS);

export const unlockUser = async (login, reason) =>
  postRequestHandler(API.UNLOCK_USER, { login, reason });

export const getExternalOrgHierarchy = async () =>
  postRequestHandler(API.GET_EXTERNAL_ORGANIZATIONS_HIERARCHY);

export const getUserContries = () => getRequestHandler(API.GET_USER_COUNTRIES);

export const getUserTerritories = () =>
  getRequestHandler(API.GET_USER_TERRITORIES);

export const getOrgDefaultRoles = organizationId => {
  if (organizationId)
    return postRequestHandler(API.GET_ORG_DEFAULT_ROLES, { organizationId: 1 });
  else return null;
};

export const getOrganizationsByCountries = (countryCodes = [], nameLike) => {
  if (countryCodes.length === 0) return [];
  return postRequestHandler(API.GET_ORGANIZATIONS_BY_COUNTRIES, {
    request: { country_codes: countryCodes },
    nameLike
  });
};

export const assignRolesToOrganization = ({
  organizationId = [],
  rolesWithPeriods = []
}) =>
  postRequestHandler(API.ASSIGN_ROLES_TO_ORGANIZATION, {
    organizationId,
    rolesWithPeriods
  });

export const getUserMetaData = ({ userId, userType }) =>
  postRequestHandler(API.GET_USER_METADATA, {
    userId,
    userType
  });

export const getUserProfileMetaData = () =>
  postRequestHandler(API.GET_USER_PROFILE_METADATA);

export const forceUserActivate = ({ login }) =>
  postRequestHandler(API.FORCE_USER_ACTIVATE, { login });

const getRequestHandler = async (url, defaultErrorMsg) => {
  try {
    const result = await axios.get(url);
    return result.data;
  } catch (error) {
    getReadableError(error, defaultErrorMsg);
  }
};

export const postRequestHandler = async (url, params, defaultErrorMsg) => {
  try {
    const result = await axios.post(url, params);
    return result.data;
  } catch (error) {
    getReadableError(error, defaultErrorMsg);
  }
};

export const getReadableError = (error, defaultMsg = text.defaultMsg) => {
  let message;
  let response = error.response;
  const grpcError = getGrpcMessage(response);

  switch (grpcError) {
    case GRPC_USER.INVALID_ARGUMENT.REASON:
      message = text.noReason;
      break;
    case GRPC_USER.FAILED_PRECONDITION.NO_USER:
    case GRPC_USER.NOT_FOUND.USER:
      message = text.noUser;
      break;

    // change password
    case GRPC_USER.INVALID_ARGUMENT.EMAIL:
      message = text.wrongEmail;
      break;
    case GRPC_USER.INVALID_ARGUMENT.NEW_PASS:
      message = text.wrongNewPass;
      break;
    case GRPC_USER.INVALID_ARGUMENT.CURRENT_PASS:
      message = text.wrongCurrentPass;
      break;
    case GRPC_USER.INVALID_ARGUMENT.GENERAL:
      message = text.noPassword;
      break;
    case GRPC_USER.INVALID_ARGUMENT.EXT_USER_NOT_ASSIGNED_TO_ANY_ORGANIZATION:
      message = text.extUserNotAssignedToAnyOrganization;
      break;
    case GRPC_USER.INVALID_ARGUMENT.USER_DOESNT_EXISTS:
      message = text.userNotExists;
      break;
    case GRPC_USER.INVALID_ARGUMENT.NOT_AUTHORIZED:
      message = text.userNotAuthorized;
      break;

    case GRPC_USER.INTERNAL_ERROR.SERVICE:
      message = text.internalError;
      break;
    case GRPC_USER.INVALID_ARGUMENT.FIRST_NAME:
      message = text.invalidFirstName;
      break;
    case GRPC_USER.INVALID_ARGUMENT.LAST_NAME:
      message = text.invalidLastName;
      break;
    case GRPC_USER.INVALID_ARGUMENT.JOB_TITLE:
      message = text.invalidJobTitle;
      break;
    case GRPC_USER.INVALID_ARGUMENT.EMPLOYER_NUMBER:
      message = text.invalidEmployerNumber;
      break;
    case GRPC_USER.INVALID_ARGUMENT.DATE_TIME:
      message = text.invalidDateTimeFormat;
      break;
    case GRPC_USER.INVALID_ARGUMENT.PHONE:
      message = text.invalidPhone;
      break;
    case GRPC_USER.INVALID_ARGUMENT.DATETIME_AFTER_NOW:
      message = text.dateTimeAfterNow;
      break;
    case GRPC_USER.ALREADY_EXISTS.GENERAL:
      message = text.userExist;
      break;
    case GRPC_USER.UNLOCK.EMPTY_ID:
      message = text.unlockEmptyId;
      break;
    case GRPC_USER.UNLOCK.REASON_NOT_FOUND:
      message = text.unlockReasonNotFound;
      break;
    case GRPC_USER.UNLOCK.USER_NOT_FOUND:
      message = text.unlockUserNotFound;
      break;
    case GRPC_USER.UNLOCK.INVALID_STATUS:
      message = text.unlockInvalidStatus;
      break;
    case GRPC_USER.UNLOCK.NO_EMAIL:
      message = text.unlockNoEmail;
      break;
    default:
      message = defaultMsg || text.generalError;
  }

  const detailedError = Error(message);
  detailedError.response = response;
  throw detailedError;
};

const getOrganizationBySearchCriteria = ({ organization, country, region }) =>
  postRequestHandler(API.GET_ORGANIZATIONS_BY_SEARCH_CRITERIA, {
    organization,
    country,
    region
  });

const getRolesForOrganizations = ({ organizations, roleName }) =>
  postRequestHandler(API.GET_ROLES_FOR_ORGANIZATIONS, {
    organizations: organizations.map(organization => {
      if (organization) {
        return { id: parseInt(organization, 10) };
      }
    }),
    roleName: roleName
  });

const getUsersByOrganization = ({ organizations }) =>
  postRequestHandler(API.GET_USERS_BY_ORGANIZATION, {
    organizations: organizations
  });

const getOrganizationsByRoles = roles =>
  postRequestHandler(API.GET_ORGANIZATIONS_BY_ROLES, {
    roles: roles.map(role => ({ roleId: parseInt(role, 10) }))
  });

const removeRolesFromOrganizations = ({ roles, organizations }) =>
  postRequestHandler(API.REMOVE_ROLES_FROM_ORGANIZATIONS, {
    organizationId: organizations.map(organizationId => {
      if (organizationId) return parseInt(organizationId, 10);
    }),
    rolesWithPeriods: roles.map(role => ({ roleId: parseInt(role, 10) }))
  });

export default {
  getOrganizationBySearchCriteria,
  removeRolesFromOrganizations,
  getOrganizationsByRoles,
  getUsersByOrganization,
  getRolesForOrganizations,
  inviteUser,
  createUser,
  createUserWithRoles,
  updateUser,
  setPassword,
  requestResetPassword,
  updatePassword,
  getUser,
  getUserProfile,
  getCurrentUser,
  checkIfUserExist,
  deleteUser,
  acceptInvitattion,
  getApplications,
  getData,
  getSessionHistory,
  getUserGroups,
  findUsers,
  findExternalUsers,
  findInternalUsers,
  activateUser,
  forceResetPassword,
  getExternalOrgHierarchy,
  getOrganizationsByCountries,
  assignRolesToOrganization,
  getUserContries,
  getUserTerritories,
  getUserMetaData,
  forceUserActivate,
  getUserProfileMetaData
};
